import { useApiQuery } from "@gladis/api-client";
import { formatEur } from "@gladis/shared";
import { ErrorState, LoadingState, StackedAreaChart } from "@gladis/ui";
import { errorSentence, tRich, useT } from "@gladis/ui/i18n";
import type { ReactNode } from "react";

import { expenseScopeOf } from "./ExpenseFilters.js";

/**
 * One line: what the household is committed to paying each month, over the last year.
 *
 * ==========================================================================================
 * WHY ONE LINE AND NOT A LINE PER CATEGORY
 * ==========================================================================================
 *
 * The question is "is this going up". A line per category answers "which category is going up", which is a
 * different and later question, and eight lines is the shape that makes the first one hard to read — the
 * total becomes something the reader has to add up by eye. `variant="lines"` with a single series draws the
 * total from zero, so its height is the figure.
 *
 * ==========================================================================================
 * WHAT THE LINE INCLUDES — EVERYTHING THE HEADLINE TOTAL DOES
 * ==========================================================================================
 *
 * Historical amounts: a bill that went up in April steps up in April, because every price change is recorded
 * rather than the past being restated at today's figures.
 *
 * AND the mortgage, which is the largest recurring expense the household has. It arrives per month from 008's
 * amortisation rather than from stored history — a mortgage instalment is derivable at any date, so there was
 * nothing to record — and it is the PAYMENT, matching the rows and the total above.
 *
 * That the line and the headline figure agree is the property worth protecting here. A chart whose last point
 * sits a few hundred euros below the number printed above it is a chart nobody trusts, and this one used to
 * be exactly that; `expenses-chart.test.ts` asserts the equality with a mortgage in both sides.
 */
export interface ExpensesChartDto {
  asOf: string;
  points: { month: string; totalInEuros: number }[];
}

/**
 * The twelve points, read once for whoever wants them.
 *
 * A HOOK RATHER THAN A FETCH INSIDE THIS COMPONENT, because the stat row above the chart shows the last step
 * of the same line as a number. Two components calling `useApiQuery` with this key is one request — TanStack
 * dedupes it — and, more to the point, one answer: a card claiming "+ € 45,00" beside a line drawn from a
 * different response is the class of disagreement this codebase spends its comments on.
 *
 * The key is under the `expenses` prefix every mutation invalidates, so entering an expense refreshes the
 * chart and the card together.
 */
export function useExpensesChart(filters?: {
  category?: string;
  member?: string;
}): ReturnType<typeof useApiQuery<ExpensesChartDto>> {
  const query = new URLSearchParams();
  if (filters?.category) query.set("category", filters.category);
  if (filters?.member) query.set("member", filters.member);
  const suffix = query.size > 0 ? `?${query.toString()}` : "";

  /*
   * THE SUFFIX IS PART OF THE KEY, so two filter selections are two cache entries rather than one that
   * keeps being overwritten — the same shape `useExpenses` uses, for the same reason.
   *
   * It stays under the `expenses` prefix every mutation invalidates, so entering an expense still
   * refreshes every filtered view of the line and not only the one on screen.
   */
  return useApiQuery<ExpensesChartDto>(["expenses", "chart", suffix], `/expenses/chart${suffix}`);
}

/**
 * @param filters - SCOPES the series, from the page's own filter in the title row. A member scope is
 *   their SHARE of each month and not the household's figure for the rows they are on — see
 *   `scoped-share.ts`, and the header note above on why the difference is not cosmetic. `state` is not
 *   among them and has no meaning here: this chart already reconstructs what was live in each month,
 *   so "show archived" is a question about a row rather than about a series.
 * @param members - only so the hint can NAME the person whose line this is. A chart that changes shape
 *   when a control is used and goes on describing the household is the reason this prop exists.
 */
export function ExpensesChart({
  filters,
  members = [],
}: {
  filters?: { category?: string; member?: string };
  members?: readonly { userId: string; displayName: string }[];
} = {}): ReactNode {
  const t = useT();
  const { data, isPending, error } = useExpensesChart(filters);
  const scope = expenseScopeOf(filters, members);

  if (isPending) return <LoadingState>{t("exp.chart.loading")}</LoadingState>;
  if (error)
    return <ErrorState>{t("exp.chart.loadFailed", { reason: errorSentence(error) })}</ErrorState>;

  const points = data.points;
  const latest = points.at(-1)?.totalInEuros ?? 0;
  const earliest = points[0]?.totalInEuros ?? 0;
  const change = latest - earliest;

  /*
   * NOTHING RECORDED MEANS NOTHING TO SAY. Twelve zeroes is a truthful flat line at the bottom of an axis
   * and a useless one — a household that has entered nothing learns that it has entered nothing, which the
   * list above already says better.
   */
  if (points.every((point) => point.totalInEuros === 0)) return null;

  return (
    <section
      aria-labelledby="expenses-trend"
      className="rounded-control border border-line bg-panel p-5"
    >
      <header className="flex flex-wrap items-baseline justify-between gap-3">
        <div>
          <h2 id="expenses-trend" className="text-xl font-semibold">
            {t("exp.chart.heading")}
          </h2>
          <p className="mt-1 text-sm text-muted">
            {scope.kind === "member"
              ? t("exp.chart.hintMember", { name: scope.name })
              : scope.kind === "joint"
                ? t("exp.chart.hintJoint")
                : t("exp.chart.hint")}
          </p>
        </div>

        {/*
          THE CHANGE, IN WORDS, because "is it going up" is the question and a reader should not have to
          measure a slope to answer it. Twelve months apart rather than month-on-month: one month's step is
          noise on a household's commitments, and a year is the span the chart shows.
        */}
        <p className="text-sm text-muted">
          {change === 0
            ? t("exp.chart.unchanged", { amount: formatEur(latest) })
            : tRich(
                "exp.chart.changed",
                {
                  change: (
                    <span className="font-semibold text-ink">
                      {change > 0 ? "+" : "−"}
                      {formatEur(Math.abs(change))}
                    </span>
                  ),
                },
                { amount: formatEur(latest) },
              )}
        </p>
      </header>

      <div className="mt-5">
        <StackedAreaChart
          variant="lines"
          series={[
            {
              id: "recurring-total",
              /*
               * "Monthly total", not "Recurring expenses". The label is what a tooltip and a legend say, and
               * on a page headed Expenses beside a list headed "Recurring expenses" the latter tells a reader
               * nothing they can act on — while this says which figure the point is. It also stopped
               * colliding with the list's heading, which an e2e assertion about layout order was counting.
               */
              label: t("exp.chart.monthlyTotal"),
              points: points.map((point) => ({ at: point.month, value: point.totalInEuros })),
            },
          ]}
          emptyMessage={t("exp.chart.empty")}
        />
      </div>
    </section>
  );
}
